import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { getEventCoordinates } from "@dnd-kit/utilities";
import { arrayMove } from "@dnd-kit/sortable";
import { motion } from "framer-motion";
const snapStartToCursor = ({ activatorEvent, draggingNodeRect, transform }) => {
    if (draggingNodeRect && activatorEvent) {
        const coords = getEventCoordinates(activatorEvent);
        if (coords) {
            const offsetX = coords.x - draggingNodeRect.left;
            const offsetY = coords.y - draggingNodeRect.top;
            return {
                ...transform,
                x: transform.x + offsetX,
                y: transform.y + offsetY - draggingNodeRect.height / 2,
            };
        }
    }
    return transform;
};
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import { Alert } from "../Alert";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { cn } from "../utils/tailwind";
import { matchesExactKey, createPlatformShortcut } from "../utils/keyboardUtils";
import { useOperationHistory } from "../utils/useOperationHistory";
import { useFilterOperators, } from "./common";
import { FilterCondition } from "./FilterCondition";
import { FilterGroup } from "./FilterGroup";
import styles from './FilterPanel.module.css';
import './FilterPanel.css';
import { useLocalizedStrings } from "../LocalizationProvider";
function assignIdsToGroup(config) {
    const kind = config.logic === 'and' ? 'And' : 'Or';
    return {
        id: uuidv4(),
        kind,
        clauses: config.conditions?.map(clause => 'field' in clause
            ? ({ id: uuidv4(), ...clause })
            : assignIdsToGroup(clause)) || []
    };
}
function removeItemById(group, id) {
    let removed = null;
    const clauses = group.clauses.reduce((acc, clause) => {
        if (clause.id === id) {
            removed = clause;
            return acc;
        }
        if (!removed && 'clauses' in clause) {
            const result = removeItemById(clause, id);
            if (result.item) {
                removed = result.item;
                acc.push(result.group);
                return acc;
            }
        }
        acc.push(clause);
        return acc;
    }, []);
    return { item: removed, group: { ...group, clauses } };
}
function addItemToGroup(group, groupId, item) {
    if (group.id === groupId) {
        return { ...group, clauses: [...group.clauses, item] };
    }
    return {
        ...group,
        clauses: group.clauses.map(clause => 'clauses' in clause
            ? addItemToGroup(clause, groupId, item)
            : clause)
    };
}
function insertItemBefore(group, targetId, item) {
    const clauses = group.clauses.reduce((acc, clause) => {
        if (clause.id === targetId) {
            acc.push(item, clause);
        }
        else if ('clauses' in clause) {
            acc.push(insertItemBefore(clause, targetId, item));
        }
        else {
            acc.push(clause);
        }
        return acc;
    }, []);
    return { ...group, clauses };
}
function isGroupById(group, id) {
    if (group.id === id)
        return true;
    return group.clauses.some(clause => 'clauses' in clause && isGroupById(clause, id));
}
function findParentGroup(group, itemId) {
    if (group.clauses.some(c => c.id === itemId))
        return group;
    for (const clause of group.clauses) {
        if ('clauses' in clause) {
            const result = findParentGroup(clause, itemId);
            if (result)
                return result;
        }
    }
    return null;
}
function findGroupById(group, groupId) {
    if (group.id === groupId)
        return group;
    for (const clause of group.clauses) {
        if ('clauses' in clause) {
            const result = findGroupById(clause, groupId);
            if (result)
                return result;
        }
    }
    return null;
}
function updateGroupById(group, groupId, updates) {
    if (group.id === groupId) {
        return { ...group, ...updates };
    }
    return {
        ...group,
        clauses: group.clauses.map(clause => 'clauses' in clause ? updateGroupById(clause, groupId, updates) : clause)
    };
}
function reorderItems(group, activeId, overId) {
    const findItemLocation = (g, itemId, path = []) => {
        const index = g.clauses.findIndex(clause => clause.id === itemId);
        if (index !== -1) {
            return { group: g, index, path };
        }
        for (let i = 0; i < g.clauses.length; i++) {
            const clause = g.clauses[i];
            if ('clauses' in clause) {
                const result = findItemLocation(clause, itemId, [...path, i]);
                if (result)
                    return result;
            }
        }
        return null;
    };
    const activeLocation = findItemLocation(group, activeId);
    const overLocation = findItemLocation(group, overId);
    if (!activeLocation || !overLocation)
        return null;
    if (activeLocation.group.id === overLocation.group.id) {
        const newClauses = arrayMove(activeLocation.group.clauses, activeLocation.index, overLocation.index);
        return updateGroupAtPath(group, activeLocation.path, { clauses: newClauses });
    }
    return null;
}
function updateGroupAtPath(group, path, updates) {
    if (path.length === 0) {
        return { ...group, ...updates };
    }
    const [index, ...restPath] = path;
    const clause = group.clauses[index];
    if ('clauses' in clause) {
        const updatedClause = updateGroupAtPath(clause, restPath, updates);
        const newClauses = [...group.clauses];
        newClauses[index] = updatedClause;
        return { ...group, clauses: newClauses };
    }
    return group;
}
function cleanupRedundantNesting(group) {
    const cleanedClauses = group.clauses.map(clause => {
        if ('clauses' in clause) {
            const cleanedNestedGroup = cleanupRedundantNesting(clause);
            if (cleanedNestedGroup.clauses.length === 1 && 'clauses' in cleanedNestedGroup.clauses[0]) {
                const onlyChild = cleanedNestedGroup.clauses[0];
                return {
                    ...onlyChild,
                    id: cleanedNestedGroup.id
                };
            }
            return cleanedNestedGroup;
        }
        return clause;
    });
    return {
        ...group,
        clauses: cleanedClauses
    };
}
function convertToReportConfig(group) {
    return {
        logic: group.kind.toLowerCase(),
        conditions: group.clauses.map(clause => {
            if ('field' in clause) {
                const { id, ...cond } = clause;
                return cond;
            }
            return convertToReportConfig(clause);
        })
    };
}
function countActiveFilters(group) {
    let count = 0;
    for (const clause of group.clauses) {
        if ('field' in clause) {
            count++;
        }
        else {
            count += countActiveFilters(clause);
        }
    }
    return count;
}
export const FilterPanel = forwardRef(({ value, onChange, availableFields = [], className, maxDepth = 3, onConfirm, instanceId, isReloading = false, onOpenChange, inline = false, fetchFieldValues, showApplicantGroupField = true, operatorsByField, }, ref) => {
    const L = useLocalizedStrings();
    const { OPERATORS_BY_TYPE } = useFilterOperators();
    const [isOpen, setIsOpen] = useState(false);
    const filterPanelRef = useRef(null);
    const [showInteractionWarning, setShowInteractionWarning] = useState(false);
    const [alertKey, setAlertKey] = useState(0);
    const [isFlashing, setIsFlashing] = useState(false);
    const addConditionButtonRef = useRef(null);
    const autoAddedCondRef = useRef(null);
    const suppressAutoAddRef = useRef(false);
    const initialTree = React.useMemo(() => {
        if (value?.config) {
            return assignIdsToGroup(value.config);
        }
        return { id: uuidv4(), kind: 'And', clauses: [] };
    }, [value?.config]);
    const mountId = React.useRef(`${Date.now()}-${Math.random()}`);
    const sessionKey = `reports-filter-${instanceId ?? 'default'}-${mountId.current}`;
    const operationsHistory = useOperationHistory(sessionKey, initialTree, 20, 1000);
    useEffect(() => {
        sessionStorage.removeItem(sessionKey);
        return () => {
            if (minorChangeTimeoutRef.current) {
                clearTimeout(minorChangeTimeoutRef.current);
                minorChangeTimeoutRef.current = null;
                if (pendingMinorChangeRef.current) {
                    operationsHistory.push(pendingMinorChangeRef.current);
                    pendingMinorChangeRef.current = null;
                }
            }
            sessionStorage.removeItem(sessionKey);
        };
    }, [sessionKey, instanceId]);
    const minorChangeTimeoutRef = useRef(null);
    const pendingMinorChangeRef = useRef(null);
    const [currentTree, setCurrentTree] = useState(operationsHistory.current);
    useEffect(() => {
        setCurrentTree(operationsHistory.current);
    }, [operationsHistory.current]);
    const tree = currentTree;
    const prevIsOpenRef = useRef(false);
    const pendingOpenAutoAddRef = useRef(false);
    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            pendingOpenAutoAddRef.current = true;
        }
        else if (!isOpen) {
            pendingOpenAutoAddRef.current = false;
        }
        prevIsOpenRef.current = isOpen;
        if (isOpen &&
            pendingOpenAutoAddRef.current &&
            tree.clauses.length === 0 &&
            availableFields.length > 0 &&
            !autoAddedCondRef.current &&
            !suppressAutoAddRef.current) {
            const defaultField = availableFields[0];
            const defaultOperator = defaultField ? OPERATORS_BY_TYPE[defaultField.type]?.[0] : null;
            const newCond = {
                id: uuidv4(),
                field: defaultField?.key || "",
                operator: defaultOperator?.value || 'equals',
                value: defaultOperator?.requiresValue ? "" : undefined
            };
            autoAddedCondRef.current = newCond;
            pendingOpenAutoAddRef.current = false;
            setCurrentTree(prev => ({ ...prev, clauses: [newCond] }));
        }
        else if (isOpen && tree.clauses.length > 0) {
            pendingOpenAutoAddRef.current = false;
        }
    }, [isOpen, tree.clauses.length, availableFields, OPERATORS_BY_TYPE]);
    useEffect(() => {
        if (!isOpen && autoAddedCondRef.current) {
            const auto = autoAddedCondRef.current;
            setCurrentTree(prev => {
                if (prev.clauses.length === 1 && 'field' in prev.clauses[0]) {
                    const cond = prev.clauses[0];
                    if (cond.id === auto.id && cond.field === auto.field &&
                        cond.operator === auto.operator &&
                        (!cond.value || cond.value === auto.value)) {
                        return { ...prev, clauses: [] };
                    }
                }
                return prev;
            });
            autoAddedCondRef.current = null;
        }
    }, [isOpen]);
    const commitPendingMinorChange = useCallback(() => {
        if (minorChangeTimeoutRef.current) {
            clearTimeout(minorChangeTimeoutRef.current);
            minorChangeTimeoutRef.current = null;
        }
        if (pendingMinorChangeRef.current) {
            operationsHistory.push(pendingMinorChangeRef.current);
            pendingMinorChangeRef.current = null;
        }
    }, [operationsHistory]);
    const [initialConfig, setInitialConfig] = useState(() => value || { enabled: true, config: { logic: 'and', conditions: [] } });
    useEffect(() => {
        setInitialConfig(value || { enabled: true, config: { logic: 'and', conditions: [] } });
    }, [value]);
    const hasUnsavedChanges = onConfirm && JSON.stringify(convertToReportConfig(tree)) !== JSON.stringify(initialConfig.config);
    const memoizedCleanup = React.useMemo(() => cleanupRedundantNesting, []);
    const emitChange = useCallback((newTree, addToHistory = true) => {
        const cleanedTree = memoizedCleanup(newTree);
        setCurrentTree(cleanedTree);
        if (addToHistory) {
            operationsHistory.push(cleanedTree);
        }
        if (cleanedTree.clauses.length === 0) {
            onChange?.({ enabled: false });
        }
        else {
            onChange?.({ enabled: true, config: convertToReportConfig(cleanedTree) });
        }
    }, [operationsHistory, onChange, memoizedCleanup]);
    const emitMajorChange = useCallback((newTree) => {
        if (minorChangeTimeoutRef.current) {
            clearTimeout(minorChangeTimeoutRef.current);
            commitPendingMinorChange();
        }
        emitChange(newTree, true);
    }, [emitChange, commitPendingMinorChange]);
    const emitMinorChange = useCallback((newTree) => {
        emitChange(newTree, false);
        pendingMinorChangeRef.current = newTree;
        if (minorChangeTimeoutRef.current) {
            clearTimeout(minorChangeTimeoutRef.current);
            minorChangeTimeoutRef.current = null;
        }
        minorChangeTimeoutRef.current = setTimeout(() => {
            if (pendingMinorChangeRef.current) {
                operationsHistory.push(pendingMinorChangeRef.current);
                pendingMinorChangeRef.current = null;
            }
            minorChangeTimeoutRef.current = null;
        }, 1000);
    }, [emitChange, operationsHistory]);
    const findLastCondition = (group) => {
        for (let i = group.clauses.length - 1; i >= 0; i--) {
            const clause = group.clauses[i];
            if ('field' in clause) {
                return clause;
            }
            else {
                const found = findLastCondition(clause);
                if (found)
                    return found;
            }
        }
        return null;
    };
    const handleAddCondition = (shouldFocusNewCondition = false) => {
        const lastCondition = findLastCondition(tree);
        const defaultFieldKey = lastCondition?.field || availableFields[0]?.key || '';
        const defaultField = availableFields.find(field => field.key === defaultFieldKey) || availableFields[0];
        const defaultOperator = defaultField ? OPERATORS_BY_TYPE[defaultField.type]?.[0] : null;
        const newCondId = uuidv4();
        const newCond = {
            id: newCondId,
            field: defaultField?.key || "",
            operator: defaultOperator?.value || 'equals',
            value: defaultOperator?.requiresValue ? "" : undefined
        };
        emitMajorChange(addItemToGroup(tree, tree.id, newCond));
        if (shouldFocusNewCondition) {
            requestAnimationFrame(() => {
                if (filterPanelRef.current) {
                    const newConditionSelect = filterPanelRef.current.querySelector(`[data-condition-id="${newCondId}"] [role="combobox"]`);
                    if (newConditionSelect) {
                        newConditionSelect.focus();
                    }
                }
            });
        }
    };
    const handleAddGroup = () => {
        if (maxDepth <= 0)
            return;
        const newGroup = {
            id: uuidv4(),
            kind: 'And',
            clauses: []
        };
        emitMajorChange(addItemToGroup(tree, tree.id, newGroup));
    };
    const handleAddApplicantGroup = () => {
        const newCond = {
            id: uuidv4(),
            field: 'applicant_group',
            operator: 'equals',
            value: ''
        };
        emitMajorChange(addItemToGroup(tree, tree.id, newCond));
    };
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: 8 },
        onActivation: () => setIsKeyboardDrag(false)
    }), useSensor(KeyboardSensor, {
        onActivation: () => setIsKeyboardDrag(true)
    }));
    const triggerFlash = useCallback(() => {
        setIsFlashing(false);
        requestAnimationFrame(() => {
            setIsFlashing(true);
            setTimeout(() => setIsFlashing(false), 1200);
        });
    }, []);
    const handleUndo = useCallback(() => {
        if (operationsHistory.canUndo) {
            const newTree = operationsHistory.undo();
            triggerFlash();
            if (newTree) {
                emitChange(newTree, false);
            }
        }
    }, [operationsHistory, triggerFlash, emitChange]);
    const handleRedo = useCallback(() => {
        if (operationsHistory.canRedo) {
            const newTree = operationsHistory.redo();
            triggerFlash();
            if (newTree) {
                emitChange(newTree, false);
            }
        }
    }, [operationsHistory, triggerFlash, emitChange]);
    const [isKeyboardDrag, setIsKeyboardDrag] = useState(false);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleKeyDown = (event) => {
            if (matchesExactKey(event, createPlatformShortcut('z'))) {
                event.preventDefault();
                handleUndo();
            }
            if (matchesExactKey(event, createPlatformShortcut('z', { shift: true }))) {
                event.preventDefault();
                handleRedo();
            }
            if (matchesExactKey(event, createPlatformShortcut('y'))) {
                event.preventDefault();
                handleRedo();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleUndo, handleRedo]);
    const customCollisionDetection = (args) => {
        if (isKeyboardDrag) {
            return closestCenter(args);
        }
        else {
            const pointerCollisions = pointerWithin(args);
            if (pointerCollisions.length === 0) {
                return closestCenter(args);
            }
            return pointerCollisions;
        }
    };
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };
    const handleDragOver = (event) => {
        const overId = event.over?.id || null;
        setOverId(overId);
    };
    const handleDragCancel = () => {
        setActiveId(null);
        setOverId(null);
        setIsKeyboardDrag(false);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setOverId(null);
        setIsKeyboardDrag(false);
        if (!over) {
            return;
        }
        const activeId = active.id;
        const overId = over.id;
        if (active.id === over.id) {
            return;
        }
        const isItemToItemMove = !overId.includes('-') && !isGroupById(tree, overId);
        if (isItemToItemMove) {
            const reorderResult = reorderItems(tree, activeId, overId);
            if (reorderResult) {
                emitMajorChange(reorderResult);
                return;
            }
            else {
                return;
            }
        }
        else {
        }
        const sourceParentGroup = findParentGroup(tree, activeId);
        const sourceKind = sourceParentGroup?.kind;
        const sourceGroupId = sourceParentGroup?.id;
        const { item, group: groupWithoutItem } = removeItemById(tree, activeId);
        if (!item) {
            return;
        }
        const isConditionItem = 'field' in item;
        let updatedTree = groupWithoutItem;
        let inserted = false;
        let destGroupId = null;
        if (overId.includes('-before-')) {
            const targetId = overId.split('-before-')[1];
            if (targetId === activeId) {
                emitChange(tree, false);
                return;
            }
            destGroupId = findParentGroup(updatedTree, targetId)?.id ?? null;
            updatedTree = insertItemBefore(updatedTree, targetId, item);
            inserted = true;
        }
        else if (overId.includes('-after-')) {
            const targetId = overId.split('-after-')[1];
            if (targetId === activeId) {
                emitChange(tree, false);
                return;
            }
            const findAndInsertAfter = (grp) => {
                const targetIndex = grp.clauses.findIndex(c => c.id === targetId);
                if (targetIndex !== -1) {
                    const newClauses = [...grp.clauses];
                    newClauses.splice(targetIndex + 1, 0, item);
                    return { group: { ...grp, clauses: newClauses }, found: true };
                }
                let found = false;
                const updatedClauses = grp.clauses.map(clause => {
                    if (!found && 'clauses' in clause) {
                        const result = findAndInsertAfter(clause);
                        if (result.found) {
                            found = true;
                            return result.group;
                        }
                    }
                    return clause;
                });
                return { group: { ...grp, clauses: updatedClauses }, found };
            };
            destGroupId = findParentGroup(updatedTree, targetId)?.id ?? null;
            const result = findAndInsertAfter(updatedTree);
            updatedTree = result.group;
            inserted = result.found;
        }
        else if (overId.includes('-between-')) {
            const betweenPart = overId.split('-between-')[1];
            const clauseIds = getAllItemIds(updatedTree.clauses);
            let secondId = '';
            for (const clauseId of clauseIds) {
                if (betweenPart.endsWith('-' + clauseId)) {
                    secondId = clauseId;
                    break;
                }
            }
            if (secondId) {
                destGroupId = findParentGroup(updatedTree, secondId)?.id ?? null;
                updatedTree = insertItemBefore(updatedTree, secondId, item);
                inserted = true;
            }
        }
        else if (overId.endsWith('-root-drop')) {
            inserted = false;
        }
        else if (isGroupById(updatedTree, overId)) {
            destGroupId = overId;
            updatedTree = addItemToGroup(updatedTree, overId, item);
            inserted = true;
        }
        if (inserted) {
            if (isConditionItem &&
                sourceKind &&
                destGroupId &&
                destGroupId !== sourceGroupId) {
                const destGroup = findGroupById(updatedTree, destGroupId);
                if (destGroup && destGroup.clauses.length <= 2 && destGroup.kind !== sourceKind) {
                    updatedTree = updateGroupById(updatedTree, destGroupId, { kind: sourceKind });
                }
            }
            emitMajorChange(updatedTree);
        }
        else {
            emitChange(tree, false);
        }
    };
    const getAllItemIds = React.useCallback((items) => {
        const ids = [];
        for (const item of items) {
            ids.push(item.id);
            if ('clauses' in item) {
                ids.push(...getAllItemIds(item.clauses));
            }
        }
        return ids;
    }, []);
    const handleGroupUpdate = (updates) => {
        emitMinorChange({ ...tree, ...updates });
    };
    const handleGroupMajorUpdate = (updates) => {
        emitMajorChange({ ...tree, ...updates });
    };
    const handleGroupMinorUpdate = (updates) => {
        emitMinorChange({ ...tree, ...updates });
    };
    const findDraggedItem = React.useCallback((itemId, searchTree) => {
        const findInGroup = (group) => {
            for (const clause of group.clauses) {
                if (clause.id === itemId) {
                    return clause;
                }
                if ('clauses' in clause) {
                    const found = findInGroup(clause);
                    if (found)
                        return found;
                }
            }
            return null;
        };
        return findInGroup(searchTree);
    }, []);
    const clearFilters = () => {
        suppressAutoAddRef.current = true;
        autoAddedCondRef.current = null;
        const emptyTree = { id: uuidv4(), kind: 'And', clauses: [] };
        emitMajorChange(emptyTree);
        if (onConfirm) {
            const emptyConfig = { enabled: true, config: convertToReportConfig(emptyTree) };
            onConfirm(emptyConfig);
            setInitialConfig(emptyConfig);
            setShowInteractionWarning(false);
        }
        requestAnimationFrame(() => {
            addConditionButtonRef.current?.focus();
        });
    };
    const toggleOpen = () => {
        setIsOpen(prev => {
            const newOpen = !prev;
            if (newOpen) {
                suppressAutoAddRef.current = false;
            }
            onOpenChange?.(newOpen);
            return newOpen;
        });
    };
    const applyConfig = useCallback((config) => {
        suppressAutoAddRef.current = true;
        autoAddedCondRef.current = null;
        const newTree = config?.config
            ? assignIdsToGroup(config.config)
            : { id: uuidv4(), kind: 'And', clauses: [] };
        emitMajorChange(newTree);
        if (onConfirm) {
            const cleaned = memoizedCleanup(newTree);
            const newConfig = cleaned.clauses.length > 0
                ? { enabled: true, config: convertToReportConfig(cleaned) }
                : { enabled: false };
            onConfirm(newConfig);
            setInitialConfig(newConfig);
            setShowInteractionWarning(false);
        }
    }, [emitMajorChange, memoizedCleanup, onConfirm]);
    const flush = useCallback(() => {
        commitPendingMinorChange();
        if (tree.clauses.length === 0) {
            onChange?.({ enabled: false });
        }
        else {
            onChange?.({ enabled: true, config: convertToReportConfig(tree) });
        }
    }, [commitPendingMinorChange, tree, onChange, convertToReportConfig]);
    useImperativeHandle(ref, () => ({
        clearFilters,
        toggleOpen,
        flush,
        applyConfig,
    }), [clearFilters, flush, applyConfig]);
    const activeFilterCount = countActiveFilters(tree);
    const hasActiveFilters = activeFilterCount > 0 || tree.clauses.length > 0;
    const filterContent = (_jsxs("div", { className: cn(inline ? "w-full" : "px-2 pt-4 pb-2"), children: [_jsxs("div", { className: "relative", children: [isFlashing && _jsx("div", { className: styles.flashOverlay }), _jsxs(DndContext, { sensors: sensors, collisionDetection: customCollisionDetection, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, onDragCancel: handleDragCancel, modifiers: [restrictToVerticalAxis], children: [_jsx(FilterGroup, { group: tree, availableFields: availableFields, onUpdate: handleGroupUpdate, onMajorUpdate: handleGroupMajorUpdate, onMinorUpdate: handleGroupMinorUpdate, isRoot: true, activeId: activeId, overId: overId, maxDepth: maxDepth, fetchFieldValues: fetchFieldValues, showApplicantGroupField: showApplicantGroupField, operatorsByField: operatorsByField }), _jsx(DragOverlay, { dropAnimation: null, modifiers: [restrictToVerticalAxis, snapStartToCursor], children: activeId ? (() => {
                                    const draggedItem = findDraggedItem(activeId, tree);
                                    if (!draggedItem)
                                        return null;
                                    if ('field' in draggedItem) {
                                        return (_jsx("div", { className: "bg-white border border-blue-500 rounded shadow-lg p-2 opacity-80", children: _jsx(FilterCondition, { condition: draggedItem, availableFields: availableFields, onUpdate: () => { }, onDelete: undefined, operatorsByField: operatorsByField }) }));
                                    }
                                    else {
                                        return (_jsx("div", { className: "bg-white border border-blue-500 rounded shadow-lg p-2 opacity-80", children: _jsxs("div", { className: "text-sm font-medium text-blue-700", children: [draggedItem.kind, " ", L.component_library.filter_panel.group, " (", draggedItem.clauses.length, " ", draggedItem.clauses.length === 1 ? 'item' : 'items', ")"] }) }));
                                    }
                                })() : null })] })] }), showInteractionWarning && hasUnsavedChanges && (_jsx(motion.div, { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.2, ease: "easeOut" }, children: _jsx(Alert, { variant: "danger", mode: "inline", className: "mb-2", children: _jsx("span", { children: L.component_library.filter_panel.confirm_or_cancel }) }) }, alertKey)), !hasActiveFilters && (_jsx("div", { className: "text-center text-sm text-gray-500 mb-2 pt-4", children: L.component_library.filter_panel.no_filters_configured })), _jsxs("div", { className: "flex justify-between space-x-8 items-start", children: [_jsxs("div", { className: "flex flex-col justify-start sm:flex-row gap-2", children: [_jsx(Button, { ref: addConditionButtonRef, variant: "secondary", size: "md", onClick: () => handleAddCondition(false), icon: _jsx(Icon, { name: "PlusIcon", type: "solid" }), children: L.component_library.filter_panel.add_condition }), maxDepth > 0 && _jsx(Button, { variant: "secondary", size: "md", onClick: handleAddGroup, icon: _jsx(Icon, { name: "SquaresPlusIcon", type: "solid" }), children: L.component_library.filter_panel.add_condition_group }), showApplicantGroupField && (_jsx(Button, { variant: "secondary", size: "md", onClick: handleAddApplicantGroup, icon: _jsx(Icon, { name: "PlusIcon", type: "solid" }), children: L.component_library.filter_panel.add_applicant_group }))] }), _jsxs("div", { className: "flex flex-col gap-2 items-center justify-end sm:flex-row", children: [hasActiveFilters && (_jsx(Button, { variant: "secondary", size: "md", onClick: clearFilters, disabled: isReloading, children: L.component_library.filter_panel.clear_all })), onConfirm && (_jsx(Button, { size: "md", disabled: isReloading, onClick: () => {
                                    const newConfig = { enabled: true, config: convertToReportConfig(tree) };
                                    onConfirm(newConfig);
                                    setInitialConfig(newConfig);
                                    setShowInteractionWarning(false);
                                    autoAddedCondRef.current = null;
                                }, children: L.component_library.filter_panel.apply }))] })] })] }));
    if (inline) {
        return filterContent;
    }
    return (_jsx("div", { "data-filter-panel-state": isOpen ? "open" : "closed", className: cn("grid transition-[grid-template-rows] duration-200 ease-in-out", isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]", className), children: _jsx("div", { ref: filterPanelRef, className: "overflow-hidden", children: filterContent }) }));
});
